import { useSearchParams } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import { DateRange, Range, RangeKeyDict } from "react-date-range"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import { Popover, Box, TextField, InputAdornment } from "@mui/material"
import EventIcon from "@mui/icons-material/Event"
import React, { useState } from "react"
import { DateFilterParams } from "./useInitialDateFilters"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"

const useStyles = makeStyles()(() => ({
    dateFilter: {
        width: "15.5rem",
    },
    popover: {
        padding: "1rem",
    },
    inputWrapper: {
        width: "100%",
    },
    input: {
        cursor: "pointer",
    },
}))

type DateRangeFilterProps = {
    value: { startDate: DateTime; endDate: DateTime }
    onChange: (newValue: { startDate: DateTime; endDate: DateTime }) => void
    paramNameStart?: DateFilterParams
    paramNameEnd?: DateFilterParams
    label?: string
    minDate?: DateTime
    maxDate?: DateTime
    inputClasses?: string
    dateFilterClasses?: string
}

export const StandardDateRangeFilter = ({
    value,
    onChange,
    paramNameStart = DateFilterParams.START,
    paramNameEnd = DateFilterParams.END,
    label,
    minDate,
    maxDate,
    inputClasses,
    dateFilterClasses,
}: DateRangeFilterProps) => {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const [searchParams, setSearchParams] = useSearchParams()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [state, setState] = useState<Range>({
        startDate: value.startDate.toJSDate() || new Date(),
        endDate: value.endDate.toJSDate() || new Date(),
        key: "selection",
    })

    const handleDateChange = (ranges: RangeKeyDict) => {
        setState(ranges["selection"])
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        const newStartDate = state.startDate ? dateTimeFactory.fromISO(state?.startDate.toISOString()) : value.startDate

        const newEndDate = state?.endDate ? dateTimeFactory.fromISO(state?.endDate.toISOString()) : value.endDate

        if (!newStartDate.equals(value.startDate) || !newEndDate.equals(value.endDate)) {
            onChange({ startDate: newStartDate, endDate: newEndDate })
            searchParams.set(paramNameStart, newStartDate.toISODate() || "")
            searchParams.set(paramNameEnd, newEndDate.toISODate() || "")
            setSearchParams(searchParams)
        }
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)

    return (
        <Box>
            <Box onClick={handleClick} className={cx(classes.dateFilter, dateFilterClasses)}>
                <TextField
                    InputProps={{
                        readOnly: true,
                        classes: { input: cx(classes.input, inputClasses) },
                        endAdornment: (
                            <InputAdornment sx={{ cursor: "pointer" }} position="end">
                                <EventIcon />
                            </InputAdornment>
                        ),
                    }}
                    className={classes.inputWrapper}
                    value={`${dateTimeFactory.formatDate(
                        dateTimeFactory.fromISO(state.startDate!.toISOString()),
                    )} - ${dateTimeFactory.formatDate(dateTimeFactory.fromISO(state.endDate!.toISOString()))}`}
                    label={label ?? l10n.getString("standard-period-date-picker")}
                />
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box className={classes.popover}>
                    <DateRange
                        onChange={handleDateChange}
                        minDate={minDate?.toJSDate()}
                        maxDate={maxDate?.toJSDate()}
                        moveRangeOnFirstSelection={false}
                        ranges={[state]}
                        months={2}
                        direction="horizontal"
                    />
                </Box>
            </Popover>
        </Box>
    )
}
